import { Link } from "react-router-dom";
import { RunStatus, SystemReviewStatus } from "./types";

export const DAG = {
  gold:    "#C9A260",
  bg:      "#0d0d0a",
  surface: "#1a1917",
  sidebar: "#111110",
  border:  "#262420",
  text:    "#E5E0D5",
  muted:   "#9D9585",
  subtle:  "#5A5448",
} as const;

// Keep K8S as alias so existing imports don't break
export const K8S = DAG;

export const inputClass =
  "w-full h-9 px-2.5 text-sm border border-[#262420] rounded-none bg-[#1a1917] text-[#E5E0D5] box-border focus:outline-none focus:ring-1 focus:ring-[#C9A260] focus:border-[#C9A260] font-mono placeholder:text-[#5A5448]";

export const selectClass = `${inputClass} cp-select`;

export const btnPrimary =
  "h-9 px-3 text-sm font-medium text-[#111110] bg-[#C9A260] hover:bg-[#B8913F] disabled:opacity-50 rounded-none cursor-pointer";

export const btnSecondary =
  "h-9 px-3 text-sm font-medium text-[#E5E0D5] bg-transparent border border-[#262420] hover:border-[#C9A260] hover:text-[#C9A260] rounded-none cursor-pointer";

export const btnDanger =
  "px-3 py-1.5 text-sm font-medium text-red-400 hover:text-red-300 cursor-pointer";

// --- Status ----------------------------------------------------------------

type StatusKind = RunStatus | SystemReviewStatus | "healthy" | "unknown";

const STATUS_META: Record<string, { dot: string; text: string; label: string }> = {
  pending:      { dot: "bg-[#5A5448]",   text: "text-[#9D9585]",   label: "Pending" },
  running:      { dot: "bg-[#C9A260]",   text: "text-[#C9A260]",   label: "Running" },
  needs_rework: { dot: "bg-amber-500",   text: "text-amber-400",   label: "NeedsRework" },
  completed:    { dot: "bg-emerald-500", text: "text-emerald-400", label: "Completed" },
  failed:       { dot: "bg-red-500",     text: "text-red-400",     label: "Failed" },
  healthy:      { dot: "bg-emerald-500", text: "text-emerald-400", label: "Healthy" },
  unknown:      { dot: "bg-[#5A5448]",   text: "text-[#9D9585]",   label: "Unknown" },
};

export const StatusChip: React.FC<{ status: StatusKind; pulse?: boolean }> = ({
  status,
  pulse,
}) => {
  const meta = STATUS_META[status] ?? STATUS_META.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${meta.text}`}>
      <span className={`h-2 w-2 rounded-full ${meta.dot} ${pulse ? "animate-pulse" : ""}`} />
      {meta.label}
    </span>
  );
};

// --- Layout primitives -----------------------------------------------------

export const PageTitle: React.FC<{
  kind: string;
  name: string;
  status?: StatusKind;
  pulse?: boolean;
  children?: React.ReactNode;
}> = ({ kind, name, status, pulse, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-[#5A5448]">
        {kind}
      </div>
      <h1 className="text-xl font-semibold text-[#E5E0D5] font-mono truncate">
        {name}
      </h1>
    </div>
    <div className="flex items-center gap-3">
      {status && <StatusChip status={status} pulse={pulse} />}
      {children}
    </div>
  </div>
);

export const Panel: React.FC<{
  title?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ title, action, children, className = "" }) => (
  <section className={`bg-[#1a1917] border border-[#262420] ${className}`}>
    {(title || action) && (
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#262420]">
        {title && (
          <h2 className="text-[10px] font-semibold uppercase tracking-widest text-[#5A5448]">
            {title}
          </h2>
        )}
        {action}
      </div>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const StatTile: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="bg-[#1a1917] border border-[#262420] px-4 py-3">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#5A5448]">
      {label}
    </div>
    <div className="mt-1 text-lg font-semibold text-[#E5E0D5] font-mono tabular-nums">
      {value}
    </div>
  </div>
);

export const AlertBanner: React.FC<{
  variant: "error" | "warn" | "ok";
  children: React.ReactNode;
}> = ({ variant, children }) => {
  const styles = {
    error: "bg-red-950/40 border-red-900/60 text-red-300",
    warn:  "bg-amber-950/40 border-amber-800/50 text-amber-300",
    ok:    "bg-emerald-950/40 border-emerald-900/60 text-emerald-300",
  };
  return (
    <div className={`border px-3 py-2 text-sm mb-4 ${styles[variant]}`}>
      {children}
    </div>
  );
};

export const ResourceLink: React.FC<{
  to: string;
  children: React.ReactNode;
}> = ({ to, children }) => (
  <Link to={to} className="text-[#C9A260] hover:text-[#E5C88A] hover:underline font-mono text-sm">
    {children}
  </Link>
);

export const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = "",
}) => (
  <th
    className={`px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-widest text-[#5A5448] bg-[#141210] border-b border-[#262420] ${className}`}
  >
    {children}
  </th>
);

export const Td: React.FC<{
  children: React.ReactNode;
  className?: string;
  mono?: boolean;
}> = ({ children, className = "", mono }) => (
  <td
    className={`px-3 py-2 text-sm text-[#E5E0D5] border-b border-[#1f1e1b] ${mono ? "font-mono" : ""} ${className}`}
  >
    {children}
  </td>
);

export const DescribeRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="grid grid-cols-[140px_1fr] gap-2 py-1.5 border-b border-[#1f1e1b] text-sm last:border-0">
    <span className="text-[#9D9585] font-medium">{label}</span>
    <span className="text-[#E5E0D5] font-mono break-all">{value}</span>
  </div>
);

export const YamlBlock: React.FC<{ children: string }> = ({ children }) => (
  <pre className="text-xs font-mono text-[#C9A260]/80 bg-[#141210] border border-[#262420] p-4 overflow-x-auto whitespace-pre-wrap break-words">
    {children}
  </pre>
);
